import React, { useEffect, useRef, useState } from "react";
import { Award, Workflow, Handshake, Clock } from "lucide-react";

const stats = [
  { icon: Award, end: 10, suffix: "+", label: "Years Experience" },
  { icon: Workflow, end: 500, suffix: "+", label: "Projects Completed" },
  { icon: Handshake, end: 100, suffix: "%", label: "Customer Satisfaction" },
  { icon: Clock, end: 100, suffix: "%", label: "Time Management" },
];

function useCountUp(end, run, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, run, duration]);
  return val;
}

const StatCard = ({ icon: Icon, end, suffix, label, run, i }) => {
  const val = useCountUp(end, run);
  return (
    <div className={`reveal d${i + 1} glow-card group rounded-3xl p-8 glass text-center hover:-translate-y-1.5 transition-all duration-300`}>
      <div className="mx-auto mb-5 h-16 w-16 rounded-full grid place-items-center bg-gradient-to-br from-orange-500 to-amber-500 shadow-[0_12px_30px_rgba(249,115,22,0.4)] group-hover:scale-110 transition">
        <Icon className="h-7 w-7 text-white" strokeWidth={2.4} />
      </div>
      <div className="font-display text-4xl md:text-5xl">
        <span className="text-gradient">{val}</span>
        <span className="text-gradient">{suffix}</span>
      </div>
      <div className="mt-2 text-sm md:text-base text-slate-400 font-semibold">{label}</div>
    </div>
  );
};

export const Stats = () => {
  const ref = useRef(null);
  const [run, setRun] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setRun(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (ref.current) io.observe(ref.current);
    // Fallback: if observer never fires (e.g. tall screens), start after 1.2s
    const t = setTimeout(() => setRun(true), 1200);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, []);

  return (
    <section id="stats" className="relative bg-[#05070f] py-16 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-orange-600/10 blur-[130px]" />
      <div ref={ref} className="relative max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {stats.map((s, i) => (
            <StatCard key={s.label} {...s} run={run} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
